import { EVENTO_SESION_VENCIDA } from '@/utils/sesion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pedidos que no usan la sesión: un 401 ahí es "contraseña incorrecta",
// no "sesión vencida".
const SIN_SESION = ['/auth/login', '/auth/register', '/auth/otp', '/auth/request-password-reset',
  '/auth/reset-password', '/auth/verify-reset-token'];

class ApiClient {
  constructor() {
    this.baseUrl = `${BACKEND_URL}/api`;
  }

  getToken() {
    return localStorage.getItem('auth_token');
  }

  async request(endpoint, options = {}, timeout = 30000) {
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      try {
        return await this._handleResponse(response);
      } catch (err) {
        // Sesión rechazada en medio del uso: se avisa para pedir la contraseña
        // sin salir de la pantalla (ModalSesionVencida).
        if (err?.status === 401 && token && !SIN_SESION.some((p) => endpoint.startsWith(p))) {
          window.dispatchEvent(new CustomEvent(EVENTO_SESION_VENCIDA));
        }
        throw err;
      }
    } catch (error) {
      // fetch() rejects with a TypeError ONLY on actual network/CORS failure
      // (typical messages: "Failed to fetch", "NetworkError when attempting to fetch resource").
      // Other TypeErrors (e.g. "body stream already read", caused by external instrumentation
      // consuming the response body) must NOT be reported as connection failures, otherwise
      // backend HTTP error messages get masked.
      const msg = error?.message || '';
      const isNetworkError =
        error instanceof TypeError &&
        (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Network request failed'));
      if (isNetworkError) {
        throw new Error('No se pudo conectar con el servidor. Verificá tu conexión o intentá más tarde.');
      }
      throw error;
    }
  }

  async _handleResponse(response) {
    let data = null;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        }
      } catch (_e) {
        // Body may already be consumed by external instrumentation (browser extensions,
        // preview wrappers, proxies). We still have the status code, so degrade
        // gracefully to a status-based error rather than a confusing network error.
        data = null;
      }
    }

    if (!response.ok) {
      if (response.status >= 500) {
        const err5 = new Error('El servidor no está disponible en este momento. Intentá de nuevo en unos minutos.');
        err5.status = response.status;
        throw err5;
      }
      // Status-based default messages (used when body cannot be read by frontend)
      const STATUS_DEFAULTS = {
        400: 'Solicitud inválida',
        401: 'Sesión expirada. Iniciá sesión de nuevo.',
        403: 'No tenés permiso para esta acción',
        404: 'Recurso no encontrado',
        409: 'Conflicto: el recurso ya existe o está en uso',
        422: 'Datos inválidos',
      };
      let errorMsg = STATUS_DEFAULTS[response.status] || `Error ${response.status}`;
      if (data) {
        if (typeof data.message === 'string') {
          errorMsg = data.message;
        } else if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          // FastAPI 422: each item has loc/msg
          errorMsg = data.detail.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; ');
        } else if (data.detail && typeof data.detail === 'object') {
          try { errorMsg = JSON.stringify(data.detail); } catch { /* keep default */ }
        }
      }
      const err = new Error(errorMsg);
      err.status = response.status;
      throw err;
    }

    return data;
  }

  // Auth
  // Sesión nueva de 30 días a partir de una válida (ver AuthContext).
  async renovarSesion() {
    return this.request('/auth/renovar-sesion', { method: 'POST' });
  }

  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(email, password, name) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
  }

  // Saldo de entrevistas (cuentas con pack) y precio del ilimitado con el
  // descuento de lo ya pagado en packs.
  async getCreditos() {
    return this.request('/cuenta/creditos');
  }

  async getMe(tokenOverride = null) {
    const token = tokenOverride || this.getToken();
    if (!token) return null;
    
    return this.request('/auth/me', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  // Evaluations
  async getEvaluations(filters = {}) {
    const params = new URLSearchParams();
    if (filters.groupId) params.set('group_id', filters.groupId);
    const qs = params.toString();
    return this.request(`/evaluations${qs ? `?${qs}` : ''}`);
  }

  async getDashboardStats() {
    return this.request('/evaluations/stats/dashboard');
  }

  async getEvaluation(id) {
    return this.request(`/evaluations/${id}`);
  }

  async createEvaluation(data) {
    return this.request('/evaluations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEvaluation(id, data) {
    return this.request(`/evaluations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Corregir valores mal anotados de una entrevista o retest. Si la corrección
  // cambia el escenario, la primera vez responde { requiere_confirmacion } sin
  // guardar; se guarda reenviando con confirmar: true.
  async corregirEvaluacion(id, data) {
    return this.request(`/evaluations/${id}/correccion`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteEvaluation(id) {
    return this.request(`/evaluations/${id}`, {
      method: 'DELETE',
    });
  }

  async assignEvaluationGroup(id, groupId) {
    return this.request(`/evaluations/${id}/group`, {
      method: 'PATCH',
      body: JSON.stringify({ group_id: groupId === null || groupId === 'none' ? null : groupId }),
    });
  }

  async toggleAptoMedico(id, entregado, intensidades) {
    const body = { entregado: !!entregado };
    if (Array.isArray(intensidades)) {
      body.intensidades = intensidades;
    }
    return this.request(`/evaluations/${id}/apto-medico`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  // Groups (privados por usuario logueado)
  async getGroups() {
    return this.request('/groups');
  }

  async createGroup(name) {
    return this.request('/groups', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateGroup(id, name) {
    return this.request(`/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  async deleteGroup(id) {
    return this.request(`/groups/${id}`, {
      method: 'DELETE',
    });
  }

  // Alumnos — entidad propia, para seguir a la misma persona en el tiempo.
  async getAlumnos({ q = '', estadoRetest = '', incluirInactivos = false } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (estadoRetest) params.set('estado_retest', estadoRetest);
    if (incluirInactivos) params.set('incluir_inactivos', 'true');
    const qs = params.toString();
    return this.request(`/alumnos${qs ? `?${qs}` : ''}`);
  }

  // Los tres números de la franja de inicio. Se calculan sobre todos los
  // alumnos activos, sin importar el filtro que tenga la pantalla.
  async getResumenAlumnos() {
    return this.request('/alumnos/resumen');
  }

  async getAlumno(id) {
    return this.request(`/alumnos/${id}`);
  }

  async getAlumnoHistorial(id) {
    return this.request(`/alumnos/${id}/historial`);
  }

  async createAlumno(data) {
    return this.request('/alumnos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAlumno(id, data) {
    return this.request(`/alumnos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAlumno(id) {
    return this.request(`/alumnos/${id}`, {
      method: 'DELETE',
    });
  }

  // Retest: ficha corta. El backend hereda de la entrevista anterior lo que no
  // se vuelve a preguntar.
  async createRetest(alumnoId, data) {
    return this.request(`/alumnos/${alumnoId}/retest`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Pruebas funcionales.
  // El contexto trae lo que la pantalla necesita antes de medir: datos
  // heredados de la última entrevista, contraindicaciones y qué intensidades
  // habilita el escenario vigente.
  async getContextoMediciones(alumnoId) {
    return this.request(`/alumnos/${alumnoId}/mediciones/contexto`);
  }

  async getMediciones(alumnoId) {
    return this.request(`/alumnos/${alumnoId}/mediciones`);
  }

  async createMedicion(alumnoId, data) {
    return this.request(`/alumnos/${alumnoId}/mediciones`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteMedicion(medicionId) {
    return this.request(`/mediciones/${medicionId}`, {
      method: 'DELETE',
    });
  }

  // PDF
  async generatePdf(pdfType, { evaluationId = null, formData = null } = {}) {
    const token = this.getToken();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(`${this.baseUrl}/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          pdf_type: pdfType,
          evaluation_id: evaluationId,
          form_data: formData,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        const msg = errData?.detail || `Error ${response.status}`;
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `informe_${pdfType}.pdf`;

      return { blob, filename };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('La generación del PDF tardó demasiado.');
      }
      throw error;
    }
  }

  // Informe de seguimiento del alumno: sus entrevistas (inicial y retests) y
  // sus pruebas funcionales. Es aparte del informe de la entrevista.
  async getInformeSeguimiento(alumnoId) {
    const token = this.getToken();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    try {
      const response = await fetch(
        `${this.baseUrl}/alumnos/${encodeURIComponent(alumnoId)}/informe-seguimiento`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        },
      );
      clearTimeout(timeoutId);
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || `Error ${response.status}`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      return { blob, filename: match ? match[1] : 'Seguimiento.pdf' };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('La generación del informe tardó demasiado.');
      }
      throw error;
    }
  }

  // Helper to download PDF - mobile compatible
  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    
    // Detect mobile/iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For iOS Safari, open in new tab (download attribute doesn't work well)
    if (isIOS || (isMobile && isSafari)) {
      // Open PDF in new tab - user can then share/save
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        // Popup blocked - try alternative
        window.location.href = url;
      }
      // Don't revoke URL immediately on mobile
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return true;
    }
    
    // Standard download for desktop and Android Chrome
    try {
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      
      // Append to body for Firefox compatibility
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
      
      return true;
    } catch (err) {
      console.error('Download failed, trying fallback:', err);
      // Fallback: open in new tab
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return true;
    }
  }

  // Fase B — Student intake tokens (profesional autenticado crea link)
  async createStudentIntakeToken() {
    return this.request('/admin/student-intake/tokens', {
      method: 'POST',
    });
  }

  // Fase C — Página pública del alumno (sin auth)
  async getStudentIntakeInfo(token) {
    return this.request(`/public/student-intake/${token}`);
  }

  async submitStudentIntake(token, payload) {
    return this.request(`/public/student-intake/${token}/submit`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // decision: { alumno_id } o { alumno_nuevo: true }, cuando hubo que elegir ficha.
  async markEvaluationReviewed(id, decision) {
    return this.request(`/evaluations/${id}/mark-reviewed`, {
      method: 'POST',
      ...(decision ? { body: JSON.stringify(decision) } : {}),
    });
  }

  // Tour de bienvenida terminado o salteado: no se vuelve a mostrar.
  async marcarTourVisto() {
    return this.request('/auth/tour-visto', { method: 'POST' });
  }

  // Alumnos de este profesional con el mismo nombre (sin tildes ni mayúsculas).
  async coincidenciasAlumno(nombre) {
    return this.request(`/alumnos/coincidencias?nombre=${encodeURIComponent(nombre || '')}`);
  }

  // Password Reset
  async requestPasswordReset(email) {
    return this.request('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  // Account cancellation (Ley 25.326)
  async cancelAccount() {
    return this.request('/auth/cancel-account', {
      method: 'POST',
    });
  }
}

export const api = new ApiClient();
